import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { 
  Building2, Users, UserPlus, LogOut, Plus, Trash2, 
  ToggleLeft, ToggleRight, Search, Pencil, KeyRound, Image, Upload, X, BookOpen,
  Crown, Star, Sparkles, Layout, Link2, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [activeTab, setActiveTab] = useState('schools');
  const [schools, setSchools] = useState([]);
  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [qbManagers, setQbManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSchoolModal, setShowCreateSchoolModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [showCreateQBMModal, setShowCreateQBMModal] = useState(false);
  const [showEditSchoolModal, setShowEditSchoolModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [schoolFormData, setSchoolFormData] = useState({
    name: '',
    logo_url: ''
  });

  const [logoPreview, setLogoPreview] = useState(null);

  const [adminFormData, setAdminFormData] = useState({
    email: '',
    password: '',
    name: '',
    school_id: ''
  });

  const [qbmFormData, setQbmFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const fetchData = async () => {
    try {
      const [schoolsRes, adminsRes, qbmRes] = await Promise.all([
        api.get('/super-admin/schools'),
        api.get('/super-admin/school-admins'),
        api.get('/super-admin/question-bank-managers')
      ]);
      setSchools(schoolsRes.data);
      setSchoolAdmins(adminsRes.data);
      setQbManagers(qbmRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle file upload for school logo
  const handleLogoUpload = async (file, isEdit = false) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG or JPG image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const uploadToast = toast.loading('Uploading logo...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/school-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Get the full URL by prepending the API base URL
      const logoUrl = response.data.logo_url;
      
      setSchoolFormData(prev => ({ ...prev, logo_url: logoUrl }));
      setLogoPreview(URL.createObjectURL(file));
      
      toast.dismiss(uploadToast);
      toast.success('Logo uploaded successfully!');
    } catch (err) {
      toast.dismiss(uploadToast);
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file, isEdit);
    }
  };

  const removeLogo = () => {
    setSchoolFormData(prev => ({ ...prev, logo_url: '' }));
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Creating school...');
    
    try {
      await api.post('/super-admin/schools', schoolFormData);
      toast.dismiss(loadingToast);
      toast.success('School created successfully!');
      setShowCreateSchoolModal(false);
      setSchoolFormData({ name: '', logo_url: '' });
      setLogoPreview(null);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to create school');
    }
  };

  const handleEditSchool = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Updating school...');
    
    try {
      await api.patch(`/super-admin/schools/${editingSchool.id}`, {
        name: schoolFormData.name,
        logo_url: schoolFormData.logo_url
      });
      toast.dismiss(loadingToast);
      toast.success('School updated successfully!');
      setShowEditSchoolModal(false);
      setEditingSchool(null);
      setLogoPreview(null);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to update school');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Creating school admin...');
    
    try {
      await api.post('/super-admin/school-admins', {
        ...adminFormData,
        role: 'school_admin'
      });
      toast.dismiss(loadingToast);
      toast.success('School admin created successfully!');
      setShowCreateAdminModal(false);
      setAdminFormData({ email: '', password: '', name: '', school_id: '' });
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to create school admin');
    }
  };

  const handleCreateQBM = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Creating Question Bank Manager...');
    
    try {
      await api.post('/super-admin/question-bank-managers', qbmFormData);
      toast.dismiss(loadingToast);
      toast.success('Question Bank Manager created successfully!');
      setShowCreateQBMModal(false);
      setQbmFormData({ email: '', password: '', name: '' });
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to create Question Bank Manager');
    }
  };

  const deleteQBM = async (managerId, managerName) => {
    if (!window.confirm(`Are you sure you want to delete "${managerName}"?`)) return;
    
    try {
      await api.delete(`/super-admin/question-bank-managers/${managerId}`);
      toast.success('Question Bank Manager deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete Question Bank Manager');
    }
  };

  const deleteSchool = async (schoolId, schoolName) => {
    if (!window.confirm(`Are you sure you want to delete "${schoolName}"? This will deactivate all users in this school.`)) return;
    
    try {
      await api.delete(`/super-admin/schools/${schoolId}`);
      toast.success('School deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete school');
    }
  };

  const toggleQuestionBankAccess = async (schoolId, schoolName, currentAccess) => {
    try {
      const response = await api.post(`/super-admin/schools/${schoolId}/toggle-question-bank-access`);
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle Question Bank access');
    }
  };

  const updateSubscriptionTier = async (schoolId, schoolName, newTier) => {
    try {
      await api.patch(`/super-admin/schools/${schoolId}`, { subscription_tier: newTier });
      toast.success(`${schoolName} upgraded to ${newTier.charAt(0).toUpperCase() + newTier.slice(1)} tier`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'premium':
        return { icon: Crown, color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Premium' };
      case 'professional':
        return { icon: Star, color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Professional' };
      default:
        return { icon: Sparkles, color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Starter' };
    }
  };

  const openEditSchoolModal = (school) => {
    setEditingSchool(school);
    setSchoolFormData({
      name: school.name,
      logo_url: school.logo_url || ''
    });
    setLogoPreview(school.logo_url || null);
    setShowEditSchoolModal(true);
  };

  const filteredSchools = schools.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAdmins = schoolAdmins.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredQBMs = qbManagers.filter(q => 
    q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Super Admin Dashboard</h1>
            <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Building2 className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Schools</p>
                <p className="text-2xl font-bold text-slate-900">{schools.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Users className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">School Admins</p>
                <p className="text-2xl font-bold text-slate-900">{schoolAdmins.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab('schools')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'schools'
                ? 'bg-indigo-700 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Schools
          </button>
          <button
            onClick={() => setActiveTab('admins')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'admins'
                ? 'bg-indigo-700 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            School Admins
          </button>
          <button
            onClick={() => setActiveTab('qbm')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'qbm'
                ? 'bg-indigo-700 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Question Bank Managers
          </button>
          <button
            onClick={() => navigate('/super-admin/cms')}
            className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600"
          >
            <Layout className="w-4 h-4" />
            Website Content
          </button>
          <button
            onClick={() => navigate('/super-admin/lti')}
            className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600"
          >
            <Link2 className="w-4 h-4" />
            LTI / Moodle
          </button>
          <button
            onClick={() => navigate('/super-admin/legal')}
            className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
          >
            <FileText className="w-4 h-4" />
            Legal Pages
          </button>
          <button
            onClick={() => navigate('/super-admin/blog')}
            className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600"
            data-testid="blog-admin-btn"
          >
            <FileText className="w-4 h-4" />
            Blog
          </button>
        </div>

        {/* Search and Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-80 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {activeTab === 'schools' && (
            <button
              onClick={() => setShowCreateSchoolModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              Create School
            </button>
          )}
          {activeTab === 'admins' && (
            <button
              onClick={() => setShowCreateAdminModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all"
            >
              <UserPlus className="w-5 h-5" />
              Create School Admin
            </button>
          )}
          {activeTab === 'qbm' && (
            <button
              onClick={() => setShowCreateQBMModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
            >
              <UserPlus className="w-5 h-5" />
              Create Question Bank Manager
            </button>
          )}
        </div>

        {/* Schools Table */}
        {activeTab === 'schools' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Logo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">School Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Subscription</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Created</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSchools.map(school => {
                  const tierInfo = getTierBadge(school.subscription_tier || 'starter');
                  const TierIcon = tierInfo.icon;
                  return (
                  <tr key={school.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      {school.logo_url ? (
                        <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-slate-900">{school.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        school.is_active !== false
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {school.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative inline-block">
                        <select
                          value={school.subscription_tier || 'starter'}
                          onChange={(e) => updateSubscriptionTier(school.id, school.name, e.target.value)}
                          className={`appearance-none pl-8 pr-8 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all ${tierInfo.color}`}
                          data-testid={`subscription-select-${school.id}`}
                        >
                          <option value="starter">Starter</option>
                          <option value="professional">Professional</option>
                          <option value="premium">Premium</option>
                        </select>
                        <TierIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" />
                        <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(school.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditSchoolModal(school)}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteSchool(school.id, school.name)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {filteredSchools.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No schools found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* School Admins Table */}
        {activeTab === 'admins' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">School</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAdmins.map(admin => (
                  <tr key={admin.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{admin.name}</td>
                    <td className="px-6 py-4 text-slate-600">{admin.email}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-sm">
                        {admin.school_name || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        admin.is_active !== false
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {admin.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAdmins.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No school admins found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Question Bank Managers Table */}
        {activeTab === 'qbm' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Created</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredQBMs.map(qbm => (
                  <tr key={qbm.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-purple-600" />
                        </div>
                        <span className="font-medium text-slate-900">{qbm.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{qbm.email}</td>
                    <td className="px-6 py-4 text-slate-500 text-sm">
                      {qbm.created_at ? new Date(qbm.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteQBM(qbm.id, qbm.name)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredQBMs.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-slate-500">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No Question Bank Managers found</p>
                      <p className="text-sm mt-1">Create one to manage your question bank</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create School Modal */}
      {showCreateSchoolModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Create New School</h2>
            </div>
            
            <form onSubmit={handleCreateSchool} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">School Name</label>
                <input
                  type="text"
                  required
                  value={schoolFormData.name}
                  onChange={(e) => setSchoolFormData({ ...schoolFormData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Springfield High School"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">School Logo (PNG or JPG)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => handleFileSelect(e, false)}
                  className="hidden"
                />
                
                {!schoolFormData.logo_url ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-500 hover:bg-indigo-50 transition-all"
                  >
                    {uploading ? (
                      <div className="text-slate-500">Uploading...</div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400" />
                        <span className="text-sm text-slate-500">Click to upload logo</span>
                        <span className="text-xs text-slate-400">PNG or JPG, max 5MB</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="relative inline-block">
                    <img 
                      src={logoPreview || schoolFormData.logo_url} 
                      alt="Logo preview" 
                      className="w-24 h-24 rounded-lg object-cover border border-slate-200" 
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateSchoolModal(false); setLogoPreview(null); setSchoolFormData({ name: '', logo_url: '' }); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                >
                  Create School
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit School Modal */}
      {showEditSchoolModal && editingSchool && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Edit School</h2>
            </div>
            
            <form onSubmit={handleEditSchool} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">School Name</label>
                <input
                  type="text"
                  required
                  value={schoolFormData.name}
                  onChange={(e) => setSchoolFormData({ ...schoolFormData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">School Logo</label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => handleFileSelect(e, true)}
                  className="hidden"
                />
                
                {!schoolFormData.logo_url ? (
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-500 hover:bg-indigo-50 transition-all"
                  >
                    {uploading ? (
                      <div className="text-slate-500">Uploading...</div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400" />
                        <span className="text-sm text-slate-500">Click to upload logo</span>
                        <span className="text-xs text-slate-400">PNG or JPG, max 5MB</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="relative inline-block">
                      <img 
                        src={logoPreview || schoolFormData.logo_url} 
                        alt="Logo preview" 
                        className="w-24 h-24 rounded-lg object-cover border border-slate-200" 
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => editFileInputRef.current?.click()}
                      disabled={uploading}
                      className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      Change Logo
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditSchoolModal(false); setEditingSchool(null); setLogoPreview(null); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create School Admin Modal */}
      {showCreateAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Create School Admin</h2>
            </div>
            
            <form onSubmit={handleCreateAdmin} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={adminFormData.name}
                  onChange={(e) => setAdminFormData({ ...adminFormData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={adminFormData.email}
                  onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={adminFormData.password}
                  onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assign to School</label>
                <select
                  required
                  value={adminFormData.school_id}
                  onChange={(e) => setAdminFormData({ ...adminFormData, school_id: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select School</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>{school.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateAdminModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-all"
                >
                  Create Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Question Bank Manager Modal */}
      {showCreateQBMModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Create Question Bank Manager</h2>
              </div>
            </div>
            
            <form onSubmit={handleCreateQBM} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={qbmFormData.name}
                  onChange={(e) => setQbmFormData({ ...qbmFormData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={qbmFormData.email}
                  onChange={(e) => setQbmFormData({ ...qbmFormData, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="qbm@school.edu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={qbmFormData.password}
                  onChange={(e) => setQbmFormData({ ...qbmFormData, password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Create a strong password"
                />
              </div>

              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>Note:</strong> Question Bank Managers can create, edit, and manage questions 
                  in the question bank. They are not tied to any specific school.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateQBMModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all"
                >
                  Create Manager
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
